import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { boolToRadio, radioToNullableBool } from "@/lib/helpers/radio-adapters";
import { Button2 } from "@/components/UI2/atoms/Button";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { Radio2 } from "@/components/UI2/atoms/Radio";
import { RadioGroup2 } from "@/components/UI2/molecules/RadioGroup";
import { testLocator } from "@/lib/helpers/test-locator";
import { TextField2 } from "@/components/UI2/molecules/TextField";

import { acaAddendumSchema, type AcaAddendumFormValues } from "./AcaAddendum.schema";
import { SCREENING_QUESTIONS } from "./AcaAddendum.constants";

type AcaAddendumFormProps = {
  isPending: boolean;
  mutate: (values: AcaAddendumFormValues) => void;
  onCancel: () => void;
};

export function AcaAddendumForm({ isPending, mutate, onCancel }: AcaAddendumFormProps) {
  const { control, formState, handleSubmit } = useForm<AcaAddendumFormValues>({
    defaultValues: {
      // Fields start untouched/unanswered (undefined) — same as the note
      // form itself — so a submit that only ever touches one question
      // leaves the other three out of the payload entirely (ky/JSON.stringify
      // drops undefined keys) rather than sending an explicit false "No".
      addendum: "",
    },
    mode: "onChange",
    resolver: zodResolver(acaAddendumSchema),
  });

  return (
    <form
      className="tw:flex tw:flex-col tw:gap-6"
      onSubmit={handleSubmit((values) => mutate(values))}
      {...getAutomationDataAttr("note-addendum-acaa-form")}
      {...testLocator("noteAddendumAcaaForm")}
    >
      <div className="tw:grid tw:grid-cols-2 tw:gap-6">
        {SCREENING_QUESTIONS.map(({ name, testId, title }) => (
          <Controller
            control={control}
            key={name}
            name={name}
            render={({ field }) => (
              <RadioGroup2
                hasStrongLabel
                label={title}
                onChange={(value) => field.onChange(radioToNullableBool(value))}
                value={boolToRadio(field.value) as string}
                {...getAutomationDataAttr(`note-addendum-acaa-${testId}`)}
                {...testLocator(`noteAddendumAcaa${testId}`)}
              >
                <Radio2 value="Yes">Yes</Radio2>
                <Radio2 value="No">No</Radio2>
              </RadioGroup2>
            )}
          />
        ))}
      </div>

      <Controller
        control={control}
        name="addendum"
        render={({ field, fieldState }) => (
          <TextField2
            errorMessage={fieldState.error?.message}
            hasStrongLabel
            isInvalid={!!fieldState.error}
            label="Details"
            mode="textarea"
            onChange={field.onChange}
            placeholder="Add details that were missed or have changed since the note was signed."
            textAreaProps={{
              className: "tw:h-32",
              ...getAutomationDataAttr("note-addendum-comment"),
              ...testLocator("noteAddendumComment"),
            }}
            value={field.value}
          />
        )}
      />

      <div className="tw:flex tw:items-end tw:justify-end tw:gap-4">
        <Button2 onPress={onCancel} variant="secondary">
          Cancel
        </Button2>

        <Button2
          isDisabled={!formState.isValid}
          isLoading={isPending}
          labelLoading={null}
          type="submit"
          variant="primary"
          {...getAutomationDataAttr("note-addendum-save")}
          {...testLocator("noteAddendumSave")}
        >
          Save
        </Button2>
      </div>
    </form>
  );
}
